import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = ["School", "Tuition / Coaching", "Solo Teacher", "Home-schooler", "Parent"];
const plans = [
  "ProBodh Platform",
  "ProBodh DNA",
  "ProBodh Quest",
  "ProBodh Focus",
  "Not sure yet",
];

function Field({ label, required, type = "text", value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
        {label} {required && "*"}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-2 w-full border-b border-line focus:border-ink py-2.5 md:py-3 text-[15px] md:text-[16px] text-ink placeholder:text-ink-muted outline-none"
        data-testid={testId}
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
        {label}
      </span>
      <div className="relative mt-2">
        <select
          value={value}
          onChange={onChange}
          className="w-full appearance-none border-b border-line focus:border-ink py-2.5 md:py-3 pr-8 text-[15px] md:text-[16px] text-ink outline-none cursor-pointer"
          data-testid={testId}
        >
          <option value="">Select one</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-ink-muted text-xs">
          ▾
        </span>
      </div>
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    plan_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and a short message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast.success("Thank you. Someone from ProBodh will be in touch shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-cream py-16 md:py-24"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="rounded-2xl md:rounded-[28px] bg-off border border-line-soft p-6 md:p-10 lg:p-14 card-shadow">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left */}
            <div className="lg:col-span-5">
              <div className="text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
                Get in touch
              </div>
              <h2 className="mt-3 md:mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-ink tracking-tight">
                Tell us what your <br className="hidden sm:block" />
                <span className="font-italic-serif text-accent">
                  classroom needs.
                </span>
              </h2>
              <p className="mt-4 md:mt-6 max-w-[42ch] text-[14px] md:text-[16px] leading-relaxed text-ink-soft">
                We don&apos;t publish pricing because no two institutions look alike.
                Send us the shape of your setup — students, staff, boards, pain
                points — and we&apos;ll come back with a plan that fits.
              </p>

              <div className="mt-8 md:mt-10 pt-6 border-t border-line">
                <div className="text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
                  Or write to us directly
                </div>
                <a
                  href="mailto:hello@probodh.com"
                  className="mt-2 inline-block font-display text-xl md:text-2xl lg:text-3xl text-ink link-underline"
                  data-testid="contact-email-link"
                >
                  hello@probodh.com
                </a>
              </div>
            </div>

            {/* Right */}
            <div className="lg:col-span-7">
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-2xl bg-mint p-8 md:p-10"
                  data-testid="contact-success"
                >
                  <div className="w-11 h-11 rounded-full bg-ink text-white-t flex items-center justify-center mb-6">
                    <Check size={20} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight text-ink">
                    Message received.
                  </h3>
                  <p className="mt-3 max-w-[42ch] text-ink/80 text-[14px] md:text-[15px] leading-relaxed">
                    A note has already landed in the ProBodh inbox. We usually
                    reply within one working day — often the same afternoon.
                  </p>
                  <button
                    onClick={() => {
                      setSent(false);
                      setForm({
                        name: "",
                        email: "",
                        phone: "",
                        organization: "",
                        role: "",
                        plan_interest: "",
                        message: "",
                      });
                    }}
                    className="mt-6 btn-pill btn-outline"
                    data-testid="contact-reset"
                  >
                    Send another
                  </button>
                </motion.div>
              ) : (
                <form
                  onSubmit={submit}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6"
                  data-testid="contact-form"
                  noValidate
                >
                  <Field
                    label="Full name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    testId="contact-input-name"
                  />
                  <Field
                    label="Email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    testId="contact-input-email"
                  />
                  <Field
                    label="Phone (optional)"
                    value={form.phone}
                    onChange={set("phone")}
                    testId="contact-input-phone"
                  />
                  <Field
                    label="Institution / Organization"
                    value={form.organization}
                    onChange={set("organization")}
                    testId="contact-input-org"
                  />
                  <Select
                    label="I represent a…"
                    value={form.role}
                    onChange={set("role")}
                    options={roles}
                    testId="contact-input-role"
                  />
                  <Select
                    label="Module of interest"
                    value={form.plan_interest}
                    onChange={set("plan_interest")}
                    options={plans}
                    testId="contact-input-plan"
                  />

                  <div className="sm:col-span-2">
                    <label className="block">
                      <span className="text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
                        What should we know? *
                      </span>
                      <textarea
                        required
                        value={form.message}
                        onChange={set("message")}
                        rows={4}
                        placeholder="Board, number of students, current tools, biggest pain point…"
                        className="mt-2 w-full border-b border-line focus:border-ink py-2.5 md:py-3 text-[15px] md:text-[16px] leading-relaxed text-ink placeholder:text-ink-muted outline-none resize-none"
                        data-testid="contact-input-message"
                      />
                    </label>
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-3">
                    <p className="text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold">
                      * Required · we reply within one working day
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-pill btn-primary disabled:opacity-60"
                      data-testid="contact-submit"
                    >
                      {loading ? "Sending…" : "Send the enquiry"}
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
